import React from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';

import { CustomButton } from '@/components/ui/custom/CustomButton';
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { ShieldAlert } from 'lucide-react';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { DetailsSectionHeader } from './DetailsSection';

function SimpleBlockDialog(props) {
	const title = props.activeConversation._customer.isBlocked
		? 'Confirm Unblock Customer'
		: 'Confirm Block Customer';

	const content = props.activeConversation._customer.isBlocked
		? 'By unblocking this number, this customer will be able to message you'
		: 'Are you sure you want to block this number? You will no longer receive messages from this customer.';

	const handleConfirmClick = async () => {
		const _customer = props.activeConversation._customer._id;

		const res = await axios.post('/api/block-customer', {
			_customer,
			isBlocked: !props.activeConversation._customer.isBlocked,
			_business: props.activeConversation._business,
		});

		if (res.data.error) {
			return;
		}

		const newConversation = {
			...props.activeConversation,
			_customer: res.data.customer,
		};

		// update the ui with newConversation and customer
		props.addConversationToCustomerConversations(newConversation, true);

		return;
	};

	const btnTitle = props.activeConversation._customer.isBlocked
		? 'Unblock'
		: 'Block';

	return (
		<AlertDialog>
			<AlertDialogTrigger asChild>
				<CustomButton variant="outline" size="sm" className="max-md:min-h-11 max-md:px-4 max-md:text-sm">
					<p className="lg:flex sm:hidden">{btnTitle}</p>
					<p className="lg:hidden sm:flex max-sm:hidden">
						{btnTitle.split(' ')[0]}
					</p>
				</CustomButton>
			</AlertDialogTrigger>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>{title}</AlertDialogTitle>
					<AlertDialogDescription>{content}</AlertDialogDescription>
				</AlertDialogHeader>

				<AlertDialogFooter>
					<AlertDialogCancel>Cancel</AlertDialogCancel>
					<AlertDialogAction onClick={() => handleConfirmClick()}>
						Confirm
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}

SimpleBlockDialog.propTypes = {
	activeConversation: PropTypes.object.isRequired,
};

function SimpleUnsubscribeDialog(props) {
	const handleConfirmClick = async () => {
		const _customer = props.activeConversation._customer._id;

		const res = await axios.post('/api/set-customer-inactive', {
			_customer,
		});

		if (res.data.error) {
			return;
		}

		const newConversation = {
			...props.activeConversation,
			_customer: res.data.customer,
		};

		// update the ui with newConversation and customer
		props.addConversationToCustomerConversations(newConversation, true);

		return;
	};

	const title =
		props.activeConversation._customer &&
		props.activeConversation._customer.active === false
			? 'Unsubscribed'
			: 'Unsubscribe';

	return (
		<AlertDialog>
			<AlertDialogTrigger asChild>
				<CustomButton
					// className="w-full"
					variant="outline"
					size="sm"
					className="max-md:min-h-11 max-md:px-4 max-md:text-sm"
					disabled={
						props.activeConversation._customer &&
						props.activeConversation._customer.active === false
					}
					style={{
						textAlign: 'left',
						display: props.activeConversation._customer.phone
							? 'block'
							: 'none',
					}}
				>
					<p className="">{title}</p>
					{/* <p className="lg:hidden md:flex">{title.split(' ')[0]}</p> */}
				</CustomButton>
			</AlertDialogTrigger>

			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>
						Confirm Unsubscribe Customer
					</AlertDialogTitle>
					<AlertDialogDescription className="space-y-1">
						<p className="text-sm mb-2">
							By unsubscribing this customer, you will no longer
							be able to message them.
						</p>

						<div className="border border-red-500 bg-red-100 rounded-lg p-2 flex items-center space-x-2">
							<span>
								<ShieldAlert className="size-6 text-red-600" />
							</span>
							<p className="text-xs">
								The only way this can be undone is by having the
								customer text START to your texting number.
							</p>
						</div>
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel>Cancel</AlertDialogCancel>
					<AlertDialogAction onClick={() => handleConfirmClick()}>
						Confirm
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
}

SimpleUnsubscribeDialog.propTypes = {
	activeConversation: PropTypes.object.isRequired,
};

export default function UnsubscribeCustomer(props) {
	// only offer this service for phones or facebook
	if (
		!(
			props.activeConversation._customer.phone ||
			props.activeConversation._customer.facebookUserId ||
			props.activeConversation._customer.instagramUserId
		)
	) {
		return null;
	}

	return (
		<div className="border-b border-border/60">
			<Collapsible>
				<DetailsSectionHeader title="Block or unsubscribe" />
				<CollapsibleContent>
					<div className="px-4 pb-3 flex flex-row items-start space-x-1.5 max-md:space-x-2">
						<SimpleBlockDialog
							activeConversation={props.activeConversation}
							addConversationToCustomerConversations={
								props.addConversationToCustomerConversations
							}
						/>

						<SimpleUnsubscribeDialog
							activeConversation={props.activeConversation}
							addConversationToCustomerConversations={
								props.addConversationToCustomerConversations
							}
						/>
					</div>
				</CollapsibleContent>
			</Collapsible>
		</div>
	);
}
